import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import type { FinancialTone } from "../types/financialUi"

interface FinancialKpiCardProps {
  title: string
  value: string
  icon: IconProp
  subtitle?: string
  tone?: FinancialTone
  helpDescription?: string
  helpBullets?: string[]
}

const toneClasses: Record<FinancialTone, { icon: string; stripe: string; value: string }> = {
  neutral: { icon: "bg-slate-100 text-slate-700", stripe: "bg-slate-300", value: "text-slate-950" },
  brand: { icon: "bg-orange-100 text-orange-700", stripe: "bg-orange-500", value: "text-slate-950" },
  positive: { icon: "bg-emerald-100 text-emerald-700", stripe: "bg-emerald-500", value: "text-emerald-700" },
  warning: { icon: "bg-amber-100 text-amber-700", stripe: "bg-amber-500", value: "text-amber-700" },
  negative: { icon: "bg-red-100 text-red-700", stripe: "bg-red-500", value: "text-red-700" },
  info: { icon: "bg-blue-100 text-blue-700", stripe: "bg-blue-500", value: "text-blue-700" },
  violet: { icon: "bg-violet-100 text-violet-700", stripe: "bg-violet-500", value: "text-violet-700" },
}

export const FinancialKpiCard: React.FC<FinancialKpiCardProps> = ({
  title,
  value,
  icon,
  subtitle,
  tone = "neutral",
  helpDescription,
  helpBullets,
}) => {
  const classes = toneClasses[tone]

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="0.5s">
      <article className="group relative min-h-36 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-orange-200 hover:shadow-lg motion-reduce:transform-none motion-reduce:transition-none 2xl:p-5">
        <div className={`absolute inset-y-0 left-0 w-1 ${classes.stripe}`} />
        
        <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-gradient-to-br from-slate-100/70 to-transparent transition-transform duration-500 group-hover:scale-125 motion-reduce:transition-none" />
        
        <div className="relative flex items-start justify-between gap-4 pl-1">
          <div className="min-w-0 flex-1">
            
          <p className="text-xs font-extrabold uppercase leading-5 tracking-[0.12em] text-slate-500 xl:text-sm">
              {title}
            </p>
           
            <p className={`mt-3 break-words text-xl font-black leading-tight xl:text-2xl ${classes.value}`}>{value}</p>
            
            {subtitle && <p className="mt-2 text-xs font-semibold leading-5 text-slate-500 xl:text-sm">{subtitle}</p>}
          </div>

          <div className="flex shrink-0 items-center gap-2.5">
            {helpDescription && (
              <PopupHelpButton
                helpKey={`pages.cashflow.kpi.${title}`}
                title={title}
                description={helpDescription}
                bullets={helpBullets ?? []}
              />
            )}
            <span
              className={`flex h-11 w-11 items-center justify-center rounded-xl shadow-sm transition-transform duration-300 group-hover:scale-110 motion-reduce:transition-none xl:h-12 xl:w-12 ${classes.icon}`}
              aria-hidden="true"
            >
              <FontAwesomeIcon icon={icon} className="text-base xl:text-lg" />
            </span>
          </div>
        </div>
      </article>
    </Animate>
  )
}
